// Story 6.1 (ADO-877) — auth/session state in a Zustand store (AC2, AC4).
//
// project-context React rules (ADR-004): "Use Zustand for all client-side state
// … no Context API for global state." This is why we use the framework-agnostic
// `@auth0/auth0-spa-js` core + this store, NOT `@auth0/auth0-react` (a React
// Context wrapper). Components read auth state by subscribing to this store
// directly; AuthProvider (a plain component) writes to it during bootstrap.

import type { User } from '@auth0/auth0-spa-js';
import { create } from 'zustand';

/**
 * - `initializing` — bootstrap in flight (default).
 * - `unconfigured` — Auth0 env is absent/incomplete; no client created (AC4).
 * - `ready` — Auth0 client created; `isAuthenticated` reflects the live session.
 * - `error` — bootstrap failed (client creation / redirect callback rejected,
 *   or Auth0 returned an `error` redirect); surfaced instead of hanging.
 * - `activation-error` — Story 6.4 (ADO-880, AC3): the redirect callback rejected
 *   with an EXPIRED / already-used set-password (activation) link. Distinct from
 *   `error` so the shell renders the friendly `<ActivationLinkExpired>` recovery
 *   surface (with a re-request action) instead of the generic sign-in-failed one.
 */
export type AuthStatus = 'initializing' | 'unconfigured' | 'ready' | 'error' | 'activation-error';

export interface AuthState {
  status: AuthStatus;
  isAuthenticated: boolean;
  /** Env keys missing when `status === 'unconfigured'` (never contains values). */
  missing: readonly string[];
  /** Human-readable reason when `status === 'error'` (never contains secrets/tokens). */
  error: string | null;
  user: User | null;
  /**
   * Begins Universal Login redirect; null until a client exists. Story 6.14
   * (ADO-1186, D9): the SINGLE sign-in entry for customers AND staff — the
   * former `signInStaff` enterprise initiator is descoped from Phase 1.
   *
   * Story 6.27 (ADO-1206) — accepts an optional `returnTo` (the intended in-app
   * deep-link path) threaded to Auth0 as `appState.returnTo`; omitted for an
   * ordinary sign-in (post-login landing stays the origin).
   */
  signIn: ((returnTo?: string) => Promise<void>) | null;
  /**
   * Story 6.27 (ADO-1206) — the in-app route to navigate to AFTER a redirect
   * callback that carried a safe `appState.returnTo` (see
   * `auth0-client.completeRedirectCallback`). Non-null only for an authenticated
   * bootstrap that consumed such a callback. The `/`-route `App` component reads
   * it, navigates the router there once, and calls {@link consumeReturnTo} to
   * clear it (one-shot — a later re-render / in-app navigation never re-triggers).
   * The bootstrap cannot navigate directly (it runs detached, outside the router),
   * so it hands the route off here per ADR-004 (Zustand for global state).
   */
  postLoginReturnTo: string | null;
  /**
   * Story 6.3 (ADO-879, AC3) — clears local session state AND ends the Auth0
   * (IdP) session; null until a client exists. The "Sign out" control renders
   * only when authenticated and this is non-null.
   */
  logout: (() => Promise<void>) | null;
  /**
   * Story 6.4 (ADO-880, AC3) — re-request a fresh set-password / activation link
   * for the given email. Non-null only while `status === 'activation-error'`.
   * Enumeration-safe by construction (resolves identically for any email); the
   * recovery surface shows the same confirmation regardless of outcome.
   */
  requestActivationLink: ((email: string) => Promise<void>) | null;
  /**
   * Story 6.5 (ADO-881, AC1/AC4) — self-service password reset trigger for the
   * "Forgot password?" surface on the logged-out sign-in entry. Non-null in the
   * `ready` state (it needs only the resolved `Auth0RuntimeConfig`, not an
   * authenticated client). Enumeration-safe by construction — resolves identically
   * for any email; degrades to a no-op send when `dbConnection` is unset (the
   * confirmation still shows). Same shape/contract as {@link requestActivationLink}.
   */
  requestPasswordReset: ((email: string) => Promise<void>) | null;
  /**
   * Story 6.6 (ADO-882, AC1/AC3) — trigger the Auth0-hosted change-password flow
   * for the AUTHENTICATED user. Unlike {@link requestPasswordReset} it takes NO
   * email argument (the email comes from the session, bound at `setReady` time).
   * Non-null ONLY in `ready && isAuthenticated` AND only when the signed-in identity
   * is an Auth0 database-connection account (a federated staff `sub` has no Auth0
   * password — the affordance is hidden for them). OUTCOME-AWARE: it rejects on an
   * initiation failure so the surface can show an RFC 7807-consistent message (AC3),
   * in deliberate contrast to the enumeration-safe {@link requestPasswordReset}.
   */
  requestPasswordChange: (() => Promise<void>) | null;

  /** Mark auth unconfigured (AC4) — no client, no session. */
  setUnconfigured: (missing: readonly string[]) => void;
  /** Mark auth ready with the resolved session + the sign-in/sign-out actions. */
  setReady: (next: {
    isAuthenticated: boolean;
    user: User | null;
    signIn: (returnTo?: string) => Promise<void>;
    /**
     * Story 6.27 (ADO-1206) — the post-login route recovered from the redirect
     * callback's `appState.returnTo` (null for an ordinary sign-in / restore).
     */
    postLoginReturnTo: string | null;
    /** Story 6.3 (AC3) — the wired logout action. */
    logout: () => Promise<void>;
    /** Story 6.5 (AC1) — the enumeration-safe self-service password-reset trigger. */
    requestPasswordReset: (email: string) => Promise<void>;
    /**
     * Story 6.6 (AC1) — the outcome-aware change-password trigger for the
     * authenticated user. Null when not authenticated, or when the signed-in
     * identity is a federated staff `sub` (no Auth0 password to change).
     */
    requestPasswordChange: (() => Promise<void>) | null;
  }) => void;
  /** Mark bootstrap as failed so the shell can surface it (never hangs on loading). */
  setError: (reason: string) => void;
  /**
   * Story 6.4 (ADO-880, AC3) — mark bootstrap as failed on an EXPIRED / already-
   * used activation link, carrying the enumeration-safe re-request action for the
   * recovery surface. Distinct terminal state from {@link setError}.
   */
  setActivationError: (requestActivationLink: (email: string) => Promise<void>) => void;
  /**
   * Story 6.27 (ADO-1206) — clear {@link postLoginReturnTo} once the router has
   * navigated to it, so the deep-link restore fires exactly once.
   */
  consumeReturnTo: () => void;
  /** Restore initial state (used on teardown / in tests). */
  reset: () => void;
}

const INITIAL: Pick<
  AuthState,
  | 'status'
  | 'isAuthenticated'
  | 'missing'
  | 'error'
  | 'user'
  | 'signIn'
  | 'logout'
  | 'requestActivationLink'
  | 'requestPasswordReset'
  | 'requestPasswordChange'
  | 'postLoginReturnTo'
> = {
  status: 'initializing',
  isAuthenticated: false,
  missing: [],
  error: null,
  user: null,
  signIn: null,
  logout: null,
  requestActivationLink: null,
  requestPasswordReset: null,
  requestPasswordChange: null,
  postLoginReturnTo: null,
};

export const useAuthStore = create<AuthState>((set) => ({
  ...INITIAL,
  setUnconfigured: (missing) =>
    set({
      status: 'unconfigured',
      isAuthenticated: false,
      missing,
      error: null,
      user: null,
      signIn: null,
      logout: null,
      requestActivationLink: null,
      requestPasswordReset: null,
      requestPasswordChange: null,
      postLoginReturnTo: null,
    }),
  setReady: ({
    isAuthenticated,
    user,
    signIn,
    logout,
    requestPasswordReset,
    requestPasswordChange,
    postLoginReturnTo,
  }) =>
    set({
      status: 'ready',
      isAuthenticated,
      user,
      signIn,
      logout,
      requestActivationLink: null,
      requestPasswordReset,
      requestPasswordChange,
      postLoginReturnTo,
      missing: [],
      error: null,
    }),
  setError: (reason) =>
    set({
      status: 'error',
      isAuthenticated: false,
      user: null,
      signIn: null,
      logout: null,
      requestActivationLink: null,
      requestPasswordReset: null,
      requestPasswordChange: null,
      postLoginReturnTo: null,
      error: reason,
    }),
  setActivationError: (requestActivationLink) =>
    set({
      status: 'activation-error',
      isAuthenticated: false,
      user: null,
      signIn: null,
      logout: null,
      requestActivationLink,
      requestPasswordReset: null,
      requestPasswordChange: null,
      postLoginReturnTo: null,
      error: null,
    }),
  consumeReturnTo: () => set({ postLoginReturnTo: null }),
  reset: () => set({ ...INITIAL }),
}));
